import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UPDATE_BANNER_ANIMATIONS } from '../../../core/constants/animations';
import {
  UPDATE_CONFIG,
  UPDATE_STATE,
  UpdateState,
  VersionPayload,
} from '../../../core/constants/update.constants';
import { UpdateService } from '../../../core/services/ui/update.service';

@Component({
  selector: 'app-update-banner',
  templateUrl: './update-banner.component.html',
  styleUrls: ['./update-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [...UPDATE_BANNER_ANIMATIONS],
})
export class UpdateBannerComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  public visible = false;
  public remainingMs = 0;
  public remainingLabel = '5:00';
  public progressPercent = 100;
  public latestVersion: VersionPayload | null = null;

  constructor(
    private updateService: UpdateService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.updateService.start();

    combineLatest([
      this.updateService.state$,
      this.updateService.remainingMs$,
      this.updateService.latestVersion$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([state, remaining, version]) => {
        this.visible = state === UPDATE_STATE.WARNING;
        this.remainingMs = remaining;
        this.remainingLabel = this.formatRemaining(remaining);
        this.progressPercent = this.computeProgress(remaining);
        this.latestVersion = version;
        this.cdr.markForCheck();
      });
  }

  public updateNow(): void {
    this.updateService.forceBlocking();
  }

  private formatRemaining(ms: number): string {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  private computeProgress(remaining: number): number {
    const ratio = remaining / UPDATE_CONFIG.WARNING_DURATION_MS;
    return Math.max(0, Math.min(100, Math.round(ratio * 100)));
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
